import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Bell, X } from 'lucide-react';
import { enableNotifications } from '../lib/oneSignalService';
import { toast } from 'sonner';

// Check if PWA/notification features are available (not blocked by in-app browsers)
const isPWASupported = () => {
  try {
    // Test localStorage availability
    localStorage.setItem('__pwa_test__', '1');
    localStorage.removeItem('__pwa_test__');

    // Check if Notification API is available
    if (typeof Notification === 'undefined') return false;

    return true;
  } catch (e) {
    return false;
  }
};

// Helper functions (migrated from oneSignal) - with safe fallbacks
const isNotificationSupported = () => typeof Notification !== 'undefined';
const isUserSubscribed = () => typeof Notification !== 'undefined' && Notification.permission === 'granted';
const isFirstTimeUser = () => {
  try {
    return !localStorage.getItem('notificationPromptShown');
  } catch (e) {
    return false; // If storage blocked, don't show prompt
  }
};
const markPromptShown = () => {
  try {
    localStorage.setItem('notificationPromptShown', 'true');
  } catch (e) {
    // Ignore storage errors
  }
};
const getNotificationPermission = () => typeof Notification !== 'undefined' ? Notification.permission : 'denied';
const isStandaloneMode = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  } catch (e) {
    return false;
  }
};

const NotificationPromptModal = ({ profile, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);

  // Don't render if PWA features are not supported
  // IMPORTANT: This check must be AFTER all hooks to avoid React hooks order violation
  if (!isPWASupported()) {
    return null;
  }

  // Auto-dismiss after 10 seconds if not interacted with
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      markPromptShown();
      onClose(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, [isOpen, onClose]);
  
  // Check if running in Android PWA standalone mode
  const isAndroidPWA = isStandaloneMode() && /Android/i.test(navigator.userAgent);
  const hasPermission = typeof Notification !== 'undefined' && Notification.permission === 'granted';

  const handleEnableNotifications = async () => {
    setLoading(true);
    
    // Safety timeout: Reset loading state after 35 seconds no matter what
    // This prevents the button from being stuck forever if something goes wrong
    const safetyTimeout = setTimeout(() => {
      console.warn('[NotificationModal] Safety timeout triggered - resetting loading state');
      setLoading(false);
      toast.error('Request timed out. Please try again.');
    }, 35000);
    
    try {
      console.log('[NotificationModal] Attempting to enable notifications...');
      
      // Enable OneSignal notifications
      const result = await enableNotifications();
      const success = result.success;
      
      // Clear safety timeout since we got a response
      clearTimeout(safetyTimeout);
      
      if (success) {
        console.log('[NotificationModal] Subscription successful');
        toast.success('🔔 Notifications enabled! You\'ll stay updated on all important events.');
        markPromptShown();
        onClose(true); // true = enabled
      } else {
        console.error('[NotificationModal] Subscription failed');
        
        // Check if permission was denied
        const permission = getNotificationPermission();
        if (permission === 'denied') {
          toast.error('Notifications are blocked. Please enable them in your browser settings.');
        } else {
          toast.error('Failed to enable notifications. Please try again or check your browser settings.');
        }
      }
    } catch (error) {
      console.error('[NotificationModal] Error enabling notifications:', error);
      clearTimeout(safetyTimeout);
      
      // Show user-friendly error message based on error type
      let errorMessage = 'Failed to enable notifications. ';
      
      if (error.message.includes('STANDALONE_MODE_DENIED')) {
        // Special case for Android PWA standalone mode
        errorMessage = '⚠️ PWA Limitation: Notifications are blocked in installed app mode. To enable:\n\n' +
                      '1. Uninstall this app from your home screen\n' +
                      '2. Open the website in Chrome browser\n' +
                      '3. Enable notifications when prompted\n' +
                      '4. Then reinstall the app';
      } else if (error.message.includes('SDK methods not available')) {
        errorMessage += 'Notification service is still loading. Please wait a moment and try again.';
      } else if (error.message.includes('Permission request failed')) {
        errorMessage += 'Could not show permission prompt. Please check your browser settings.';
      } else if (error.message.includes('Opt-in failed')) {
        errorMessage += 'Could not complete subscription. Please try again.';
      } else if (error.message.includes('timed out')) {
        errorMessage += 'Request timed out. Please try again.';
      } else {
        errorMessage += error.message || 'Please try again or check your browser settings.';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMaybeLater = () => {
    markPromptShown();
    onClose(false); // false = declined
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-md"
        data-testid="notification-prompt-modal"
      >
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Bell className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">
            Stay Updated!
          </DialogTitle>
          <DialogDescription className="text-center">
            Get instant notifications about:
          </DialogDescription>
        </DialogHeader>
        
        {/* Warning for Android PWA users without permission */}
        {isAndroidPWA && !hasPermission && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm">
            <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">⚠️ PWA Mode Detected</p>
            <p className="text-amber-800 dark:text-amber-300 text-xs">
              Notifications may not work in installed app mode. If you get an error, please open this site in Chrome browser first to enable notifications, then reinstall the app.
            </p>
          </div>
        )}

        <div className="py-4">
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex-shrink-0 text-xl">💬</span>
              <span><strong>New messages</strong> from employers or job seekers</span>
            </li>
            {profile?.role === 'employer' && (
              <>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 text-xl">📋</span>
                  <span><strong>New job applications</strong> for your postings</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 text-xl">✅</span>
                  <span><strong>Application updates</strong> and responses</span>
                </li>
              </>
            )}
            {profile?.role === 'owner' && (
              <>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 text-xl">💼</span>
                  <span><strong>New bids</strong> on your gigs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 text-xl">🤝</span>
                  <span><strong>Bid updates</strong> and contract offers</span>
                </li>
              </>
            )}
            {(profile?.role === 'job_seeker' || profile?.role === 'contractor') && (
              <>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 text-xl">👀</span>
                  <span><strong>Profile views</strong> and messages</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 text-xl">📬</span>
                  <span><strong>Application status</strong> updates</span>
                </li>
              </>
            )}
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex-shrink-0 text-xl">⭐</span>
              <span><strong>Important updates</strong> and opportunities</span>
            </li>
          </ul>
        </div>

        <DialogFooter className="sm:flex-col sm:space-x-0 gap-2">
          <Button
            onClick={handleEnableNotifications}
            disabled={loading}
            className="w-full rounded-xl"
            size="lg"
            data-testid="enable-notifications-btn"
          >
            <Bell className="mr-2 h-4 w-4" />
            {loading ? 'Enabling...' : 'Enable Notifications'}
          </Button>
          <Button
            onClick={handleMaybeLater}
            variant="ghost"
            className="w-full rounded-xl"
            size="lg"
            data-testid="maybe-later-btn"
          >
            Maybe Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationPromptModal;
